// ConfirmDialog — lightweight yes/no confirmation in the app's modal style.
// For destructive-but-cheap actions (delete a chat, discard a draft) where the
// type-the-name DeleteConfirmDialog would be overkill.
import React from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    onConfirm: () => void | Promise<void>;
    isBusy?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = 'Bekräfta',
    cancelLabel = 'Avbryt',
    destructive = false,
    onConfirm,
    isBusy = false,
}) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md z-[70]">
            <DialogHeader>
                <DialogTitle className={destructive ? 'flex items-center gap-2 text-red-600' : ''}>
                    {destructive && <AlertTriangle className="h-5 w-5" />}
                    {title}
                </DialogTitle>
                {description && <DialogDescription>{description}</DialogDescription>}
            </DialogHeader>
            <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
                    {cancelLabel}
                </Button>
                <Button
                    variant={destructive ? 'destructive' : 'default'}
                    onClick={onConfirm}
                    disabled={isBusy}
                >
                    {isBusy ? '…' : confirmLabel}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);

export default ConfirmDialog;
